/**
 * System-Mediated Simulation Engine for Care360 Digital Twin
 * 
 * This engine leverages the existing HealthSnapshotV2 parameter weights system
 * to create health system-mediated effects rather than direct parameter-to-parameter
 * relationships. This approach maintains consistency with the established clinical
 * framework and eliminates the need for separate relationship mapping.
 * 
 * Architecture:
 * 1. Lifestyle changes affect health system scores
 * 2. Health system improvements propagate to all parameters in that system
 * 3. Parameter changes are proportional to existing S-F-R weights
 * 4. All calculations use HealthSnapshotV2 methodology for consistency
 */

import { calculateDynamicTarget, calculateZScore_V2 } from '../../utils/scoringEngineV2';

/**
 * LIFESTYLE PARAMETER PROCESSING DISABLED
 *
 * Lifestyle parameter impact on health systems has been temporarily disabled
 * to focus simulation on health systems parameters only (vitals, labs, biometrics).
 *
 * This configuration can be re-enabled when lifestyle domain functionality
 * is needed in the simulation sandbox.
 */

// LIFESTYLE_SYSTEM_IMPACTS configuration disabled - empty object to prevent errors
export const LIFESTYLE_SYSTEM_IMPACTS = {
  // DISABLED: All lifestyle parameter impacts have been removed to focus on health systems only
  // This empty object prevents errors in existing code that references LIFESTYLE_SYSTEM_IMPACTS

  /* COMMENTED OUT - Original lifestyle parameter configurations:

  // Exercise impacts multiple health systems
  lifestyle_movement_moderate_activity_days: {
    systemEffects: {
      cardiovascular: { impactPerDay: 1.2, maxImprovement: 8.0, evidenceLevel: 1 },
      metabolic: { impactPerDay: 0.8, maxImprovement: 6.0, evidenceLevel: 1 },
      musculoskeletal: { impactPerDay: 1.0, maxImprovement: 7.0, evidenceLevel: 1 },
      respiratory: { impactPerDay: 0.6, maxImprovement: 4.0, evidenceLevel: 2 },
      immune: { impactPerDay: 0.4, maxImprovement: 3.0, evidenceLevel: 2 }
    }
  },

  // Nutrition quality impacts
  lifestyle_nutrition_protein_intake: {
    systemEffects: {
      metabolic: { impactPerPoint: 2.0, maxImprovement: 10.0, evidenceLevel: 1 },
      cardiovascular: { impactPerPoint: 1.5, maxImprovement: 7.5, evidenceLevel: 1 },
      immune: { impactPerPoint: 0.8, maxImprovement: 4.0, evidenceLevel: 2 }
    }
  },

  // Sleep quality impacts
  lifestyle_recovery_sleep_quality: {
    systemEffects: {
      metabolic: { impactPerPoint: 2.5, maxImprovement: 12.5, evidenceLevel: 1 },
      neurological: { impactPerPoint: 2.0, maxImprovement: 10.0, evidenceLevel: 1 },
      cardiovascular: { impactPerPoint: 1.0, maxImprovement: 5.0, evidenceLevel: 2 },
      immune: { impactPerPoint: 1.5, maxImprovement: 7.5, evidenceLevel: 1 }
    }
  },

  // Stress/emotional health impacts
  lifestyle_emotion_stress_level: {
    systemEffects: {
      cardiovascular: { impactPerPoint: -1.8, maxImpact: -9.0, evidenceLevel: 1 },
      metabolic: { impactPerPoint: -1.2, maxImpact: -6.0, evidenceLevel: 1 },
      immune: { impactPerPoint: -1.0, maxImpact: -5.0, evidenceLevel: 1 },
      neurological: { impactPerPoint: -1.5, maxImpact: -7.5, evidenceLevel: 1 }
    }
  }
  */
};

/**
 * System-Mediated Simulation Engine
 */
export class SystemMediatedSimulationEngine {
  constructor() {
    this.MAX_Z_FOR_ZERO_HEALTH = 6.0; // Consistent with HealthSnapshotV2
  }

  /**
   * Main simulation function using system-mediated approach
   */
  async runSystemMediatedSimulation(
    sliderValues,
    baselineValues,
    patientDemographics,
    allScoringConfig,
    parameterWeights,
    targetFormulas
  ) {
    const startTime = performance.now();

    // Step 1: Calculate baseline health system scores
    const baselineSystemScores = await this.calculateHealthSystemScores(
      baselineValues,
      patientDemographics,
      allScoringConfig,
      parameterWeights,
      targetFormulas
    );

    // Step 2: Calculate final health system scores with new parameter values
    // This directly shows the impact of parameter changes on health systems
    const finalSystemScores = await this.calculateHealthSystemScores(
      sliderValues,
      patientDemographics,
      allScoringConfig,
      parameterWeights,
      targetFormulas
    );

    // Step 3: Calculate system improvements by comparing baseline vs final
    const systemImprovements = this.calculateDirectSystemImprovements(
      baselineSystemScores,
      finalSystemScores
    );

    // Step 4: Propagate system improvements to other parameters
    const adjustedParameterValues = await this.propagateSystemImprovementsToParameters(
      sliderValues,
      baselineValues,
      baselineSystemScores,
      finalSystemScores,
      parameterWeights,
      allScoringConfig,
      patientDemographics,
      targetFormulas
    );
    
    // Step 5: Generate comprehensive results
    return {
      baselineSystemScores,
      systemImprovements: systemImprovements,
      improvedSystemScores: finalSystemScores, // Use final scores as improved scores
      adjustedParameterValues,
      finalSystemScores,
      systemMediatedEffects: this.generateSystemMediatedEffects(
        baselineValues,
        adjustedParameterValues,
        systemImprovements
      ),
      processingStats: {
        executionTime: performance.now() - startTime,
        systemsAffected: Object.keys(systemImprovements).length,
        parametersAdjusted: Object.keys(adjustedParameterValues).length - Object.keys(sliderValues).length
      }
    };
  }

  /**
   * Calculate health system scores using EXACT HealthSnapshotV2 methodology
   *
   * CRITICAL CORRECTION: S-F-R weights are PERCENTAGES (0-100%), not absolute values.
   * HealthSnapshotV2 calculates separate Structure, Function, Risk scores for each system,
   * then averages them to get the final system score.
   *
   * Evidence from scoringEngineV2.js line 463:
   * const specificImpactSystem = absZScore * (finalWeight / 100);
   *
   * Mathematical Formula:
   * 1. For each parameter: absZScore = |zScore|
   * 2. For each S-F-R dimension: weightedContribution = absZScore * weight (weight is 0-100%)
   * 3. Dimension accumulation: sumWeightedAbsZ += weightedContribution, sumOfWeights += weight
   * 4. Dimension score: averageWeightedAbsZ = sumWeightedAbsZ / sumOfWeights
   * 5. Dimension health score: max(0, 100 - (averageWeightedAbsZ / 6.0) * 100)
   * 6. Final system score: (structureScore + functionScore + riskScore) / 3
   */
  async calculateHealthSystemScores(
    parameterValues,
    patientDemographics,
    allScoringConfig,
    parameterWeights,
    targetFormulas
  ) {
    // Initialize system deviations with separate S-F-R dimensions (EXACT HealthSnapshotV2 structure)
    const systemDeviations = {
      neurological: {
        structure: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        function: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        risk: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] }
      },
      cardiovascular: {
        structure: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        function: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        risk: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] }
      },
      respiratory: {
        structure: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        function: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        risk: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] }
      },
      metabolic: {
        structure: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        function: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        risk: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] }
      },
      filtration: {
        structure: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        function: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        risk: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] }
      },
      musculoskeletal: {
        structure: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        function: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        risk: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] }
      },
      immune: {
        structure: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        function: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] },
        risk: { sumWeightedAbsZ: 0, sumOfWeights: 0, count: 0, contributingParams: [] }
      }
    };

    // Process each parameter using EXACT HealthSnapshotV2 methodology
    for (const paramConfig of allScoringConfig) {
      const paramKey = paramConfig.key;
      const currentValue = parameterValues[paramKey];

      // Process parameter for health system impact calculation

      if (currentValue === null || currentValue === undefined) {
        continue;
      }

      // Calculate dynamic target and z-score using existing functions
      const targetResult = calculateDynamicTarget(paramKey, patientDemographics, targetFormulas);
      if (targetResult.error) {
        continue;
      }

      const zScoreResult = calculateZScore_V2(currentValue, targetResult.value, paramConfig);
      if (zScoreResult.error) {
        continue;
      }

      // Get parameter weight configuration
      const weightConfig = parameterWeights[paramKey];
      if (!weightConfig?.systems) {
        continue;
      }

      const absZScore = Math.abs(zScoreResult.zScore);

      // Process System Weights - EXACT replication of HealthSnapshotV2 logic with S-F-R separation
      for (const systemKey in weightConfig.systems) {
        if (systemDeviations[systemKey]) {
          for (const dimKey in weightConfig.systems[systemKey]) {
            const systemDimWeightInfo = weightConfig.systems[systemKey][dimKey];

            // Map dimension keys to standard S-F-R format
            let standardDimKey = dimKey.toLowerCase();
            if (standardDimKey === 's') standardDimKey = 'structure';
            else if (standardDimKey === 'f') standardDimKey = 'function';
            else if (standardDimKey === 'r') standardDimKey = 'risk';

            // CRITICAL: Match exact HealthSnapshotV2 logic with percentage interpretation
            if (systemDeviations[systemKey][standardDimKey] &&
                typeof systemDimWeightInfo === 'number' &&
                systemDimWeightInfo > 0) {

              const finalWeight = systemDimWeightInfo; // This is a percentage (0-100%)

              // EXACT formula from HealthSnapshotV2 (scoringEngineV2.js lines 459-460)
              systemDeviations[systemKey][standardDimKey].sumWeightedAbsZ += absZScore * finalWeight;
              systemDeviations[systemKey][standardDimKey].sumOfWeights += finalWeight;
              systemDeviations[systemKey][standardDimKey].count++;

              // Track contributing parameters for transparency
              systemDeviations[systemKey][standardDimKey].contributingParams.push({
                key: paramKey,
                absZ: absZScore,
                weight: finalWeight, // Percentage value
                weightedContribution: absZScore * finalWeight
              });

            }
          }
        }
      }
    }

    // Calculate final health system scores using EXACT HealthSnapshotV2 formula
    const systemScores = {};

    Object.keys(systemDeviations).forEach(systemKey => {
      const systemData = systemDeviations[systemKey];

      // Calculate health score for each S-F-R dimension (EXACT HealthSnapshotV2 methodology)
      const dimensionScores = {};
      let totalContributingParams = 0;
      let totalWeight = 0;

      ['structure', 'function', 'risk'].forEach(dimKey => {
        const data = systemData[dimKey];

        if (data.sumOfWeights > 0) {
          // SIMULATION-ENHANCED FORMULA: Create differential system impacts based on clinical weights
          //
          // The key insight: In simulation, we want systems with higher clinical relevance
          // (higher S-F-R weights) to show proportionally larger changes when parameters change.
          //
          // Standard HealthSnapshotV2: averageWeightedAbsZ = sumWeightedAbsZ / sumOfWeights
          // This normalizes weights, making all systems respond equally to the same parameter change.
          //
          // Simulation Enhancement: Scale the impact by the total system weight to create
          // differential responses that reflect clinical importance.

          // SIMULATION-SPECIFIC ENHANCEMENT:
          // To create differential impacts based on clinical significance (weights),
          // we avoid normalizing by sumOfWeights, which would make all systems respond equally.
          // Instead, we use a fixed denominator that represents an "average" system's weight contribution.
          // This ensures that systems with higher total weights (e.g., Cardiovascular)
          // show a proportionally larger deviation for the same parameter change.
          
          // A baseline of 100 represents a system where its parameters have an average weight of 100%.
          // This acts as a scaling factor to keep the resulting health score in a reasonable range.
          const SIMULATION_IMPACT_DENOMINATOR = 100; 

          // The resulting averageWeightedAbsZ is now proportional to the total weight of the system's parameters.
          data.averageWeightedAbsZ = data.sumOfWeights > 0 ? data.sumWeightedAbsZ / SIMULATION_IMPACT_DENOMINATOR : 0;
          data.healthScore = Math.max(0, 100 - (data.averageWeightedAbsZ / this.MAX_Z_FOR_ZERO_HEALTH) * 100);

          // Debug logging for NaN detection
          if (isNaN(data.averageWeightedAbsZ) || isNaN(data.healthScore)) {
            // console.error(`[SystemMediatedSimulation] NaN detected in health score calculation:`, {
            //   sumWeightedAbsZ: data.sumWeightedAbsZ,
            //   sumOfWeights: data.sumOfWeights,
            //   averageWeightedAbsZ: data.averageWeightedAbsZ,
            //   healthScore: data.healthScore
            // });
          }
        } else {
          data.averageWeightedAbsZ = 0;
          data.healthScore = 100; // No weighted contributing params, assume 100% health
        }

        dimensionScores[dimKey] = data.healthScore;
        totalContributingParams += data.count;
        totalWeight += data.sumOfWeights;
      });

      // Calculate final system score by averaging S-F-R scores (EXACT HealthSnapshot.js methodology)
      // From HealthSnapshot.js lines 47-50:
      // systemHealthPercentage = (structureHealthPercentage + functionHealthPercentage + riskHealthPercentage) / 3;
      const finalSystemScore = (dimensionScores.structure + dimensionScores.function + dimensionScores.risk) / 3;

      systemScores[systemKey] = {
        score: Math.round(finalSystemScore * 100) / 100, // Round to 2 decimal places
        structureScore: Math.round(dimensionScores.structure * 100) / 100,
        functionScore: Math.round(dimensionScores.function * 100) / 100,
        riskScore: Math.round(dimensionScores.risk * 100) / 100,
        contributingParameterCount: totalContributingParams,
        totalWeight: totalWeight,
        dimensionDetails: {
          structure: systemData.structure,
          function: systemData.function,
          risk: systemData.risk
        }
      };

    });

    // ENHANCED VALIDATION: Verify differential health system impacts for vitals_sbp
    if (Object.keys(parameterValues).includes('vitals_sbp')) {
      // this.validateDifferentialSystemImpacts(systemScores, parameterValues);
    }

    return systemScores;
  }

  /**
   * Validate differential health system impacts for Systolic BP changes
   * Ensures cardiovascular system shows significantly larger changes than respiratory system
   */
  validateDifferentialSystemImpacts(systemScores, parameterValues) {
    if (!systemScores.cardiovascular || !systemScores.respiratory) {
      // console.warn('[SystemMediatedSimulation] Missing cardiovascular or respiratory scores for validation');
      return;
    }

    const cardiovascularScore = systemScores.cardiovascular.score;
    const respiratoryScore = systemScores.respiratory.score;
    const sbpValue = parameterValues.vitals_sbp;

    // Calculate expected differential based on S-F-R weights
    // Cardiovascular: S:53 + F:92 + R:96 = 241
    // Respiratory: S:5 + F:15 + R:20 = 40
    // Expected ratio: 241/40 = 6.025 (raw weights)
    // Realistic simulation ratio: 1.5-2.5x (after scaling)

    // console.log(`[SystemMediatedSimulation] 🔍 DIFFERENTIAL IMPACT VALIDATION for SBP=${sbpValue}:`);
    // console.log(`  Cardiovascular Score: ${cardiovascularScore.toFixed(2)}`);
    // console.log(`  Respiratory Score: ${respiratoryScore.toFixed(2)}`);

    // Validation logic: Cardiovascular should generally be more affected by BP changes
    const cardiovascularMoreAffected = Math.abs(100 - cardiovascularScore) > Math.abs(100 - respiratoryScore);
    const differentialRatio = Math.abs(100 - cardiovascularScore) / Math.abs(100 - respiratoryScore);

    if (cardiovascularMoreAffected && differentialRatio > 1.2) {
      // console.log(`  ✅ VALIDATION PASSED: Cardiovascular shows ${differentialRatio.toFixed(2)}x larger impact`);
      // console.log(`  ✅ Differential impact is working correctly with S-F-R weights`);
    } else {
      // console.warn(`  ⚠️  VALIDATION WARNING: Differential impact may be insufficient`);
      // console.warn(`  ⚠️  Ratio: ${differentialRatio.toFixed(2)}x (expected >1.2x for clinical significance)`);
    }

    return {
      cardiovascularScore,
      respiratoryScore,
      differentialRatio,
      validationPassed: cardiovascularMoreAffected && differentialRatio > 1.2
    };
  }

  /**
   * Calculate direct system improvements by comparing baseline vs final system scores
   * This shows the actual impact of parameter changes on health systems
   *
   * CRITICAL DEBUGGING: Enhanced to identify why all systems show identical improvements
   * when they should show different responses based on S-F-R weights
   */
  calculateDirectSystemImprovements(baselineSystemScores, finalSystemScores) {
    const systemImprovements = {};

    // Track score differences for analysis
    const scoreDifferences = [];
    let allScoresIdentical = true;
    let firstImprovement = null;

    // Compare each health system's before/after scores
    Object.keys(baselineSystemScores).forEach(systemKey => {
      const baselineScore = baselineSystemScores[systemKey]?.score;
      const finalScore = finalSystemScores[systemKey]?.score;

      // Enhanced validation for score values
      if (baselineScore === undefined || finalScore === undefined ||
          isNaN(baselineScore) || isNaN(finalScore) ||
          !isFinite(baselineScore) || !isFinite(finalScore)) {
        // console.error(`[SystemMediatedSimulation] Invalid system scores for ${systemKey}:`, {
        //   baselineScore,
        //   finalScore,
        //   baselineData: baselineSystemScores[systemKey],
        //   finalData: finalSystemScores[systemKey]
        // });
        return; // Skip this system
      }

      const improvement = finalScore - baselineScore;
      scoreDifferences.push({ system: systemKey, improvement });

      // Check if this improvement differs from the first one
      if (firstImprovement === null) {
        firstImprovement = improvement;
      } else if (Math.abs(improvement - firstImprovement) > 0.001) {
        allScoresIdentical = false;
      }

      // Include all systems with valid numbers, even small changes
      // For single-parameter scenarios, small changes are still meaningful
      if (!isNaN(improvement) && isFinite(improvement)) {
        systemImprovements[systemKey] = {
          totalImprovement: improvement,
          sources: [{
            parameter: 'direct_parameter_change',
            change: improvement,
            improvement: improvement,
            mechanism: 'Direct parameter impact on health system',
            evidenceLevel: 'clinical_measurement'
          }]
        };
      } else {
      }
    });

    return systemImprovements;
  }

  /**
   * Merge system improvements from different sources
   */
  mergeSystemImprovements(directImprovements, lifestyleImprovements) {
    const merged = { ...directImprovements };

    // Add lifestyle improvements to direct improvements
    Object.entries(lifestyleImprovements).forEach(([systemKey, improvement]) => {
      if (merged[systemKey]) {
        // Combine improvements
        merged[systemKey].totalImprovement += improvement.totalImprovement;
        merged[systemKey].sources = [...merged[systemKey].sources, ...improvement.sources];
      } else {
        // Add new system improvement
        merged[systemKey] = improvement;
      }
    });

    return merged;
  }

  /**
   * Calculate system improvements from lifestyle changes
   * MODIFIED: Returns empty object since lifestyle processing is disabled
   */
  calculateSystemImprovements(sliderValues, baselineValues) {
    const systemImprovements = {};

    // LIFESTYLE PROCESSING DISABLED: Return empty improvements
    // This prevents lifestyle parameter processing while maintaining function compatibility

    /* ORIGINAL LOGIC COMMENTED OUT:
    // Process ONLY the lifestyle parameters that have defined impacts
    Object.keys(LIFESTYLE_SYSTEM_IMPACTS).forEach(paramKey => {
      const currentValue = sliderValues[paramKey];
      const baselineValue = baselineValues[paramKey] || 0;
      const change = currentValue - baselineValue;

      if (Math.abs(change) < 0.001) return; // No significant change

      const lifestyleImpact = LIFESTYLE_SYSTEM_IMPACTS[paramKey];
      // This check is now implicitly handled by the loop's source

      // Calculate impact on each affected health system
      Object.entries(lifestyleImpact.systemEffects).forEach(([systemKey, effect]) => {
        if (!systemImprovements[systemKey]) {
          systemImprovements[systemKey] = {
            totalImprovement: 0,
            sources: []
          };
        }

        // Calculate improvement based on parameter type
        let improvement = 0;
        if (effect.impactPerDay !== undefined) {
          improvement = change * effect.impactPerDay;
        } else if (effect.impactPerPoint !== undefined) {
          improvement = change * effect.impactPerPoint;
        }

        // Apply maximum improvement limits
        const maxImprovement = effect.maxImprovement || effect.maxImpact || 0;
        if (maxImprovement > 0) {
          improvement = Math.min(improvement, maxImprovement);
        } else if (maxImprovement < 0) {
          improvement = Math.max(improvement, maxImprovement);
        }

        systemImprovements[systemKey].totalImprovement += improvement;
        systemImprovements[systemKey].sources.push({
          parameter: paramKey,
          change,
          improvement,
          mechanism: effect.mechanism,
          evidenceLevel: effect.evidenceLevel
        });
      });
    });
    */

    return systemImprovements;
  }

  /**
   * Apply system improvements to baseline system scores
   */
  applySystemImprovements(baselineSystemScores, systemImprovements) {
    const improvedScores = { ...baselineSystemScores };

    Object.entries(systemImprovements).forEach(([systemKey, improvement]) => {
      if (improvedScores[systemKey]) {
        const newScore = Math.min(100, Math.max(0, 
          improvedScores[systemKey].score + improvement.totalImprovement
        ));
        
        improvedScores[systemKey] = {
          ...improvedScores[systemKey],
          score: newScore,
          improvement: improvement.totalImprovement,
          sources: improvement.sources
        };
      }
    });

    return improvedScores;
  }

  /**
   * Propagate system improvements to individual parameters based on their S-F-R weights
   *
   * CRITICAL: This function must use the EXACT same S-F-R weight processing as HealthSnapshotV2
   * to ensure parameter adjustments are proportional to their actual contribution to health systems.
   *
   * Key Clarifications:
   * 1. S-F-R weights are NOT averaged or summed - each dimension contributes independently
   * 2. overall_clinical_significance_weight is NOT used in HealthSnapshotV2 system calculations
   * 3. Parameter impact is proportional to its total weighted contribution across all S-F-R dimensions
   * 4. System improvements propagate based on parameter's relative weight within that system
   */
  async propagateSystemImprovementsToParameters(
    sliderValues,
    baselineValues,
    baselineSystemScores,
    improvedSystemScores,
    parameterWeights,
    allScoringConfig,
    patientDemographics,
    targetFormulas
  ) {
    const adjustedValues = { ...sliderValues };

    // For each parameter that wasn't directly adjusted by sliders
    for (const paramConfig of allScoringConfig) {
      const paramKey = paramConfig.key;

      // Skip parameters that are direct lifestyle inputs, as they drive the simulation
      if (LIFESTYLE_SYSTEM_IMPACTS.hasOwnProperty(paramKey)) continue;

      // Skip if no weight configuration
      const weightConfig = parameterWeights[paramKey];
      if (!weightConfig?.systems) continue;

      // Calculate parameter's weighted contribution to system improvements
      let totalParameterAdjustment = 0;
      let adjustmentSources = [];

      // Process each system where this parameter has weights
      for (const systemKey in weightConfig.systems) {
        const systemImprovement = improvedSystemScores[systemKey]?.improvement || 0;
        if (Math.abs(systemImprovement) < 0.001) continue;

        // Calculate parameter's impact using CORRECTED percentage-based methodology
        // CRITICAL: S-F-R weights are percentages (0-100%), not absolute values
        const systemWeights = weightConfig.systems[systemKey];
        let totalParameterImpactFromSystem = 0;

        // Process each S-F-R dimension separately (matching HealthSnapshotV2)
        for (const dimKey in systemWeights) {
          const weight = systemWeights[dimKey]; // This is a percentage (0-100%)
          if (typeof weight === 'number' && weight > 0) {
            // CORRECTED: Convert percentage to decimal and apply to system improvement
            // This matches the percentage interpretation from scoringEngineV2.js line 463
            const dimensionImpact = systemImprovement * (weight / 100) * 0.05; // 5% scale factor
            totalParameterImpactFromSystem += dimensionImpact;
          }
        }

        totalParameterAdjustment += totalParameterImpactFromSystem;
        adjustmentSources.push({
          system: systemKey,
          systemImprovement,
          totalParameterImpactFromSystem,
          weightBreakdown: systemWeights,
          calculationMethod: 'percentage_based_corrected'
        });
      }

      // Apply adjustment to parameter value if significant
      if (Math.abs(totalParameterAdjustment) > 0.001) {
        // Get baseline value and ideal target
        const baselineValue = baselineValues[paramKey];
        if (baselineValue === null || baselineValue === undefined) continue;

        const targetResult = calculateDynamicTarget(paramKey, patientDemographics, targetFormulas);
        if (targetResult.error) continue;

        const idealTarget = targetResult.value;

        // Convert health score improvement to parameter value adjustment
        const adjustedValue = this.convertHealthScoreToParameterValue(
          baselineValue,
          idealTarget,
          totalParameterAdjustment,
          paramConfig
        );

        adjustedValues[paramKey] = adjustedValue;
      }
    }

    return adjustedValues;
  }

  /**
   * Convert health score improvement to actual parameter value adjustment
   */
  convertHealthScoreToParameterValue(baselineValue, idealTarget, healthScoreImprovement, paramConfig) {
    // Determine if parameter should move toward or away from ideal target
    const isHigherBetter = this.isHigherValueBetter(paramConfig.key);
    const currentDeviation = baselineValue - idealTarget;

    // Calculate adjustment magnitude based on health score improvement
    // Larger improvements = larger parameter adjustments toward ideal
    const adjustmentFactor = healthScoreImprovement / 100; // Convert percentage to factor
    const maxAdjustment = Math.abs(currentDeviation) * 0.3; // Max 30% of current deviation

    let adjustment = maxAdjustment * adjustmentFactor;

    // Apply adjustment in direction toward ideal target
    if (currentDeviation > 0 && !isHigherBetter) {
      // Value is above ideal and lower is better - decrease value
      adjustment = -Math.abs(adjustment);
    } else if (currentDeviation < 0 && isHigherBetter) {
      // Value is below ideal and higher is better - increase value
      adjustment = Math.abs(adjustment);
    } else if (currentDeviation > 0 && isHigherBetter) {
      // Value is above ideal but higher is better - small increase
      adjustment = Math.abs(adjustment) * 0.1;
    } else if (currentDeviation < 0 && !isHigherBetter) {
      // Value is below ideal but lower is better - small decrease
      adjustment = -Math.abs(adjustment) * 0.1;
    }

    const adjustedValue = baselineValue + adjustment;

    // Apply reasonable bounds based on parameter type
    return this.applyParameterBounds(adjustedValue, paramConfig);
  }

  /**
   * Determine if higher values are better for a parameter
   */
  isHigherValueBetter(paramKey) {
    const higherBetterParams = [
      'labs_lipid_hdl',
      'vitals_vo2max',
      'vitals_hrv',
      'biometrics_strength_handgrip'
      // REMOVED: lifestyle parameters (lifestyle_movement_moderate_activity_days, etc.)
    ];

    const lowerBetterParams = [
      'vitals_sbp',
      'vitals_dbp',
      'vitals_rhr',
      'labs_lipid_ldl',
      'labs_lipid_trig',
      'labs_glucose_glucose',
      'labs_glucose_hba1c',
      'labs_inflammation_hscrp',
      'vitals_weight' // Generally for overweight patients
      // REMOVED: lifestyle_emotion_stress_level
    ];

    if (higherBetterParams.some(param => paramKey.includes(param))) return true;
    if (lowerBetterParams.some(param => paramKey.includes(param))) return false;

    // Default assumption based on parameter type
    // REMOVED: lifestyle parameter check since lifestyle parameters are disabled
    // if (paramKey.includes('lifestyle_')) return true; // Most lifestyle factors are better when higher
    return false; // Conservative default
  }

  /**
   * Apply reasonable bounds to parameter values
   */
  applyParameterBounds(value, paramConfig) {
    // Apply basic physiological limits
    const bounds = {
      'vitals_sbp': { min: 80, max: 200 },
      'vitals_dbp': { min: 50, max: 120 },
      'vitals_rhr': { min: 45, max: 120 },
      'vitals_weight': { min: 40, max: 200 },
      'labs_glucose_glucose': { min: 60, max: 300 },
      'labs_glucose_hba1c': { min: 4.0, max: 12.0 },
      'labs_lipid_ldl': { min: 30, max: 300 },
      'labs_lipid_hdl': { min: 20, max: 100 },
      'labs_lipid_trig': { min: 30, max: 500 }
    };

    const paramBounds = bounds[paramConfig.key];
    if (paramBounds) {
      return Math.max(paramBounds.min, Math.min(paramBounds.max, value));
    }

    return value;
  }

  /**
   * Generate system-mediated effects summary
   */
  generateSystemMediatedEffects(baselineValues, adjustedValues, systemImprovements) {
    const effects = {};

    Object.keys(adjustedValues).forEach(paramKey => {
      const baselineValue = baselineValues[paramKey];
      const adjustedValue = adjustedValues[paramKey];

      if (baselineValue !== undefined && Math.abs(adjustedValue - baselineValue) > 0.001) {
        effects[paramKey] = {
          baselineValue,
          adjustedValue,
          change: adjustedValue - baselineValue,
          changePercent: baselineValue !== 0 ? ((adjustedValue - baselineValue) / baselineValue) * 100 : 0,
          affectedSystems: this.getAffectedSystems(paramKey, systemImprovements)
        };
      }
    });

    return effects;
  }

  /**
   * Get which systems affected this parameter
   */
  getAffectedSystems(paramKey, systemImprovements) {
    const affectedSystems = [];

    Object.entries(systemImprovements).forEach(([systemKey, improvement]) => {
      if (Math.abs(improvement.totalImprovement) > 0.001) {
        affectedSystems.push({
          system: systemKey,
          improvement: improvement.totalImprovement,
          sources: improvement.sources.map(s => s.parameter)
        });
      }
    });

    return affectedSystems;
  }
}

export default SystemMediatedSimulationEngine;
